import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isEqual, max, omit } from 'lodash';
import { Grid, Col, Row } from 'react-bootstrap';
import numeral from 'numeral';

import ActorItem from '../../components/SectionedItems/ActorItem';
import ComponentHeader from '../../components/Headers/component-header';
import InlineQuery from '../../components/InlineQuery';
import SectionedItems from '../../components/SectionedItems';
import Table from '../../components/Table';

import {
  topShowsColumns,
  defaultSorts,
  affinityColumns,
  affinityDefaultSorts,
  formatGenderDemo
} from './table-config';

import actorsConfig from '../configs/actors-config';
import {
  CountryDemo,
  AgeDemo,
  GenderDemo
} from '../../components/Charts/DemoCharts';
import { downloadReportFunc } from '../../utils/export';
import ModuleExport from '../../components/ModuleExport';

import ActorInfo from '../../components/ActorInfo/ActorInfo';
import Iframe from '../../components/Iframe';
import { TabbedFilters, FilterTypes } from '../../components/Filters';

import { formatActorsFromQuery, formatFanVotesScore } from '../../utils/actor';

import {
  extractFilterValues,
  getChangedFilters,
  isExpectedFilters,
  getFilterNames
} from '../../utils/filters';

import './styles/ActorInsights.css';

class ActorReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      changedFilters: [],
      filters: []
    };
  }

  componentDidMount() {
    this.updateActor();
  }

  componentDidUpdate(prevProps) {
    const prevActorId = get(prevProps, 'match.params.actorId', null);
    const nextActorId = get(this.props, 'match.params.actorId', null);
    const actorHasChanged = prevActorId !== nextActorId;

    let { filters: appliedFilterValues } = this.state;
    if (actorHasChanged) {
      this.updateActor();
      appliedFilterValues = [];
      this.setState({ filters: appliedFilterValues });
    } else {
      if (!this.props.blocked) {
        const { fields: filters, groupings } = this.props.filters;
        if (!isEmpty(filters) && !isEmpty(groupings)) {
          const filterValues = extractFilterValues(filters);
          if (!isEqual(filterValues, appliedFilterValues)) {
            this.setState({ filters: filterValues });
            const {
              queryActions: { runFormattedInlineQueriesForPage }
            } = this.props;
            const changedFilterValues = getChangedFilters(
              filterValues,
              appliedFilterValues
            );
            this.updateQueries(
              filters,
              changedFilterValues,
              runFormattedInlineQueriesForPage
            );
          }
        }
      }
    }
  }

  componentWillUnmount() {
    this.resetFilters();
  }

  resetFilters() {
    const {
      filtersActions: { resetFilters }
    } = this.props;

    resetFilters();
  }

  updateActor() {
    const {
      fetchActor,
      match: {
        params: { actorId }
      },
      filtersActions: { getFiltersForPageAsync }
    } = this.props;
    this.resetFilters();
    getFiltersForPageAsync(actorsConfig.ACTOR_PAGE_ID);
    fetchActor(actorId);
  }

  updateQueries(filters, changedFilterValues, inlineQuery) {
    const changedFilterFields = getFilterNames(changedFilterValues);
    const pageConfig = { ...actorsConfig.ACTOR_REPORT_FILTERS };

    const { filterOverrides, queriesToRemove } = this.getIndexQueries(
      pageConfig,
      changedFilterFields
    );

    pageConfig.queries = omit(pageConfig.queries, queriesToRemove);

    inlineQuery(pageConfig, {
      ...filters,
      ...filterOverrides
    });
  }

  getIndexQueries(pageConfig, changedFilterFields) {
    const filterOverrides = {};
    const queriesToRemove = [];

    if (pageConfig) {
      if (
        !isExpectedFilters(
          pageConfig.queries.actorCountryIndex.fields,
          changedFilterFields
        )
      ) {
        queriesToRemove.push('actorCountryIndex');
      }

      if (
        !isExpectedFilters(
          pageConfig.queries.actorAgeIndex.fields,
          changedFilterFields
        )
      ) {
        queriesToRemove.push('actorAgeIndex');
      }

      if (
        !isExpectedFilters(
          pageConfig.queries.actorGenderIndex.fields,
          changedFilterFields
        )
      ) {
        queriesToRemove.push('actorGenderIndex');
      }
    }

    return {
      filterOverrides,
      queriesToRemove
    };
  }

  renderActorInfo() {
    const {
      actor,
      queries: { actorFanFavoriteVotes }
    } = this.props;

    const externalLinks = [
      {
        text: 'TV Time',
        url: `https://people.tvtime.com/people/${actor.id}`
      }
    ];

    if (actor) {
      document.title = `${actor.name} | Actor | CVM Insights`;
    } else {
      document.title = 'Loading | CVM Insights';
    }

    return (
      <ActorInfo
        externalLinks={externalLinks}
        name={actor.name}
        pictureUrl={actor.image}
        showsCount={actor.numShows}
        moviesCount={actor.numMovies}
        fanFavoriteVotes={formatFanVotesScore(actorFanFavoriteVotes)}
      />
    );
  }

  render() {
    const {
      actor,
      blocked,
      filters,
      queries: {
        topPerformingTitles,
        actorFavorites,
        actorShowAffinity,
        actorCountryIndex,
        actorAgeIndex,
        actorGenderIndex
      }
    } = this.props;

    if (!actor) {
      return null;
    }

    return (
      <>
        {this.renderActorInfo()}

        {blocked && (
          <Iframe
            blocked
            blockedMessage="Contact your Client Success Representative to learn more about this report"
            url=""
            height="100%"
          />
        )}

        {!blocked && (
          <>
            <TabbedFilters defaultState />
            <section className="tvt-main__content actor-report-page">
              <Grid fluid componentClass="section">
                <Row>
                  <Col xs={12} md={6}>
                    <ComponentHeader
                      title={
                        <span>
                          People Who Vote for <em>{get(actor, 'name', '')}</em>{' '}
                          Also Love
                        </span>
                      }
                    />
                    <InlineQuery query={actorFavorites}>
                      {query => (
                        <SectionedItems
                          className="tvt-actor-report__fan-favorites"
                          hasSeparators={false}
                          itemType={ActorItem}
                          list={formatActorsFromQuery(query)}
                        />
                      )}
                    </InlineQuery>
                    <ComponentHeader
                      title={
                        <span>
                          People Who Vote for <em>{get(actor, 'name', '')}</em>{' '}
                          Also Watch
                        </span>
                      }
                    />
                    <InlineQuery useChildForLoading query={actorShowAffinity}>
                      {query => (
                        <Table
                          data={query.data}
                          headerFormatter={affinityColumns}
                          defaultSorts={affinityDefaultSorts}
                          view="actorShowAffinity"
                          filters={filters.fields}
                          showRank={false}
                          height={370}
                          queryMeta={
                            actorsConfig.ACTOR_REPORT_FILTERS.queries
                              .actorShowAffinity
                          }
                        />
                      )}
                    </InlineQuery>
                    <div className="actor_report__demo-chart-wrapper">
                      <ComponentHeader
                        title={
                          <span>
                            Country Index of <em>{get(actor, 'name', '')}</em>{' '}
                            Fans
                          </span>
                        }
                      />
                      <ModuleExport
                        fetchData
                        tableDataDownload={downloadReportFunc(
                          'actorCountryIndex',
                          filters.fields,
                          actorsConfig.ACTOR_REPORT_FILTERS.queries
                            .actorCountryIndex,
                          'index'
                        )}
                        csv
                        xls
                        png
                      />
                      <div className="tvt-actor-report__countryDemo-chart-wrapper">
                        <CountryDemo
                          maxValue={max(
                            actorCountryIndex.data.map(c => Math.abs(c.index))
                          )}
                          query={actorCountryIndex}
                          keys={['index']}
                          indexKey="country"
                          valueKey="index"
                        />
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <ComponentHeader
                      title={
                        <span>
                          <em>{get(actor, 'name', '')}</em>'s Top Performing
                          Titles
                        </span>
                      }
                    />
                    <InlineQuery useChildForLoading query={topPerformingTitles}>
                      {query => (
                        <Table
                          data={query.data}
                          headerFormatter={topShowsColumns}
                          defaultSorts={defaultSorts}
                          view="topPerformingTitles"
                          filters={filters.fields}
                          showRank={false}
                          height={440}
                          queryMeta={
                            actorsConfig.ACTOR_REPORT_FILTERS.queries
                              .topPerformingTitles
                          }
                        />
                      )}
                    </InlineQuery>
                    <div className="actor_report__demo-chart-wrapper">
                      <ComponentHeader
                        title={
                          <span>
                            Age of <em>{actor.name}</em>'s Fans
                          </span>
                        }
                        tooltip="This metric represents a normalized view of
                        this defined audience. It is calculated
                        relative to the overall app population to
                        adjust for app population biases. The
                        percentages represented do not reflect raw
                        viewer demographics."
                      />
                      <ModuleExport
                        fetchData
                        tableDataDownload={downloadReportFunc(
                          'actorAgeIndex',
                          filters.fields,
                          actorsConfig.ACTOR_REPORT_FILTERS.queries
                            .actorAgeIndex,
                          'age'
                        )}
                        csv
                        xls
                        png
                      />
                      <div className="tvt-actor-report__ageDemo-chart-wrapper">
                        <AgeDemo
                          query={actorAgeIndex}
                          layout="vertical"
                          sortKey="age"
                          indexKey="age"
                          width={570}
                          keys={['index']}
                          tooltipFormatter={({ value }) =>
                            `${numeral(value * 100).format('0.0')}%`
                          }
                        />
                      </div>
                    </div>
                    <div className="actor_report__demo-chart-wrapper">
                      <ComponentHeader
                        title={
                          <span>
                            Gender of <em>{actor.name}</em>'s Fans
                          </span>
                        }
                        tooltip="This metric represents a normalized view of
                        this defined audience. It is calculated
                        relative to the overall app population to
                        adjust for app population biases. The
                        percentages represented do not reflect raw
                        viewer demographics."
                      />
                      <ModuleExport
                        fetchData
                        tableDataDownload={downloadReportFunc(
                          'actorGenderIndex',
                          filters.fields,
                          actorsConfig.ACTOR_REPORT_FILTERS.queries
                            .actorGenderIndex
                        )}
                        csv
                        xls
                        png
                      />
                      <div className="tvt-actor-report__genderDemo-chart-wrapper">
                        <GenderDemo
                          id="actor_vote_gender_index.user_gender"
                          label="gender"
                          value="index"
                          layout="vertical"
                          width={570}
                          height={288}
                          query={{
                            loading: actorGenderIndex.loading,
                            data: formatGenderDemo(actorGenderIndex)
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Grid>
            </section>
          </>
        )}
      </>
    );
  }
}

ActorReport.propTypes = {
  actor: PropTypes.shape({}),

  blocked: PropTypes.bool.isRequired,

  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  fetchActor: PropTypes.func.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPageAsync: PropTypes.func.isRequired
  }).isRequired,

  queries: PropTypes.shape({
    actorFanFavoriteVotes: PropTypes.shape({}),
    actorFavorites: PropTypes.shape({}),
    actorShowAffinity: PropTypes.shape({}),
    actorCountryIndex: PropTypes.shape({}),
    actorAgeIndex: PropTypes.shape({}),
    actorGenderIndex: PropTypes.shape({}),
    topPerformingTitles: PropTypes.shape({})
  }).isRequired,

  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired
};

ActorReport.defaultProps = {
  actor: null
};

export default ActorReport;
